import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Divider,
} from '@mui/material';
import { PaymentTransactionType, getPaymentTypeDisplayName } from '@/types/paymentTransaction';
import { useI18n } from '@/hooks/useI18n';

interface PaymentTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (transactionData: PaymentTransactionFormData) => Promise<void>;
  type: PaymentTransactionType;
  isLoading?: boolean;
  error?: string | null;
  billGrandTotal: number;
  currentPaidAmount: number;
}

export interface PaymentTransactionFormData {
  amount: number;
  personName?: string;
  reference?: string;
  paymentMethod?: string;
  description?: string;
}

const PaymentTransactionDialog: React.FC<PaymentTransactionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  type,
  isLoading = false,
  error = null,
  billGrandTotal,
  currentPaidAmount
}) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState<PaymentTransactionFormData>({
    amount: 0,
    personName: '',
    reference: '',
    paymentMethod: '',
    description: ''
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setFormData({
        amount: 0,
        personName: '',
        reference: '',
        paymentMethod: '',
        description: ''
      });
      setFormErrors({});
    }
  }, [open]);

  const handleInputChange = (field: keyof PaymentTransactionFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'amount' ? parseFloat(value) || 0 : value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.amount || formData.amount <= 0) {
      errors.amount = 'Betrag muss größer als 0 sein';
    }

    if (type === PaymentTransactionType.RECEIVED && formData.amount > (billGrandTotal - currentPaidAmount)) {
      errors.amount = 'Betrag kann nicht größer sein als der offene Betrag';
    }

    if (!formData.personName?.trim()) {
      errors.personName = 'Person ist erforderlich';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleConfirm = async () => {
    if (!validateForm()) {
      return;
    }

    await onConfirm(formData);
  };

  const remainingAmount = billGrandTotal - currentPaidAmount;
  const isValidAmount = formData.amount > 0 && 
    (type === PaymentTransactionType.SENT || formData.amount <= remainingAmount);

  const paymentMethods = [
    'Überweisung',
    'Bar',
    'PayPal',
    'Kreditkarte',
    'Lastschrift',
    'Sonstiges'
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {type === PaymentTransactionType.RECEIVED ? 'Zahlungseingang hinzufügen' : 'Zahlungsausgang hinzufügen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Bill Information */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('bill.billInformation')}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Gesamtbetrag: €{billGrandTotal.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Bereits bezahlt: €{currentPaidAmount.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Verbleibend: €{remainingAmount.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography 
                      variant="body2" 
                      color={type === PaymentTransactionType.RECEIVED ? 'success.main' : 'error.main'}
                      fontWeight="bold"
                    >
                      {getPaymentTypeDisplayName(type)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Amount */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Betrag *"
                value={formData.amount || ''}
                onChange={handleInputChange('amount')}
                disabled={isLoading}
                error={!!formErrors.amount}
                helperText={formErrors.amount}
                inputProps={{
                  min: 0,
                  step: 0.01
                }}
                InputProps={{
                  endAdornment: <Typography variant="body2">€</Typography>
                }}
              />
            </Grid>

            {/* Person */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Person *"
                value={formData.personName}
                onChange={handleInputChange('personName')}
                disabled={isLoading}
                error={!!formErrors.personName}
                helperText={formErrors.personName || 'Name der Person, die die Zahlung durchgeführt hat'}
                placeholder="z.B. Max Mustermann"
              />
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Zahlungsmethode</InputLabel>
                <Select
                  value={formData.paymentMethod}
                  onChange={handleInputChange('paymentMethod')}
                  disabled={isLoading}
                  label="Zahlungsmethode"
                >
                  <MenuItem value="">
                    <em>Bitte wählen</em>
                  </MenuItem>
                  {paymentMethods.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Reference */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Referenz"
                value={formData.reference}
                onChange={handleInputChange('reference')}
                disabled={isLoading}
                helperText="Referenz für Kontoauszug (z.B. Verwendungszweck)"
                placeholder={t('bill.referencePlaceholder')}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Beschreibung"
                value={formData.description}
                onChange={handleInputChange('description')}
                disabled={isLoading}
                helperText="Optionale zusätzliche Informationen"
                placeholder="z.B. Teilzahlung für Skipass"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading || !isValidAmount || !formData.personName?.trim()}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          color={type === PaymentTransactionType.RECEIVED ? 'success' : 'error'}
        >
          {type === PaymentTransactionType.RECEIVED ? 'Zahlungseingang' : 'Zahlungsausgang'} hinzufügen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentTransactionDialog;